/** Filtros registros guardados — inventario de gestión (patrón similar a Invoice.js). */
import React from 'react';
import { Form } from 'react-bootstrap';
import { ESTADOS_DISPONIBILIDAD, TIPOS_MOVIMIENTO_GESTION, tipoMovimientoLabel } from './inventarioEstados';

export const MESES_NOMBRE = [
  { value: '1', label: 'Enero' },
  { value: '2', label: 'Febrero' },
  { value: '3', label: 'Marzo' },
  { value: '4', label: 'Abril' },
  { value: '5', label: 'Mayo' },
  { value: '6', label: 'Junio' },
  { value: '7', label: 'Julio' },
  { value: '8', label: 'Agosto' },
  { value: '9', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' },
];

export const FILTROS_GUARDADAS_VACIOS = {
  nf: '',
  dia: '',
  mes: '',
  anio: '',
  oc: '',
  tipoNf: '',
  grado: '',
  espesor: '',
  ancho: '',
  largo: '',
  proveedor: '',
  almacen: '',
  tipoMovimiento: '',
  disponibilidadProducto: '',
  producto: '',
  booking: '',
};

const filterFieldStyle = { flex: '1 1 140px', minWidth: 0, maxWidth: '220px' };

export function foldTexto(s) {
  return String(s ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

function matchIncludes(valor, criterio) {
  if (!criterio) return true;
  return foldTexto(valor).includes(foldTexto(criterio));
}

export function aplicarFiltrosGuardadas(rows, filtros) {
  const f = { ...FILTROS_GUARDADAS_VACIOS, ...filtros };
  return (rows || []).filter((r) => {
    if (f.nf && !matchIncludes(r.nro_nf, f.nf)) return false;
    if (f.oc && !matchIncludes(r.oc_label, f.oc)) return false;
    if (f.tipoNf && String(r.tipo_nf ?? '') !== String(f.tipoNf)) return false;
    if (f.grado && !matchIncludes(r.grado, f.grado)) return false;
    if (f.booking && !matchIncludes(r.booking, f.booking)) return false;
    if (f.proveedor && !matchIncludes(r.proveedor, f.proveedor)) return false;
    if (f.almacen && !matchIncludes(r.almacen, f.almacen)) return false;
    if (f.producto && !matchIncludes(r.producto_nombre, f.producto)) return false;
    if (f.espesor && !matchIncludes(r.espesor, f.espesor)) return false;
    if (f.ancho && !matchIncludes(r.ancho, f.ancho)) return false;
    if (f.largo && !matchIncludes(r.largo, f.largo)) return false;
    if (f.tipoMovimiento && foldTexto(r.tipo_movimiento) !== foldTexto(f.tipoMovimiento)) return false;
    if (f.disponibilidadProducto && foldTexto(r.estado_mercaderia) !== foldTexto(f.disponibilidadProducto)) {
      return false;
    }
    if (f.dia || f.mes || f.anio) {
      if (!r.fecha_nf) return false;
      const d = new Date(`${String(r.fecha_nf).slice(0, 10)}T12:00:00`);
      if (Number.isNaN(d.getTime())) return false;
      if (f.dia && String(d.getDate()) !== String(parseInt(f.dia, 10))) return false;
      if (f.mes && String(d.getMonth() + 1) !== String(parseInt(f.mes, 10))) return false;
      if (f.anio && String(d.getFullYear()) !== String(parseInt(f.anio, 10))) return false;
    }
    return true;
  });
}

function filtrosExcluyendo(filtros, campo) {
  return { ...filtros, [campo]: '' };
}

function uniqSorted(values, numeric = false) {
  const set = new Set(values.filter((v) => v != null && String(v).trim() !== '').map(String));
  const arr = [...set];
  if (numeric) return arr.sort((a, b) => Number(a) - Number(b));
  return arr.sort((a, b) => a.localeCompare(b, 'es', { numeric: true }));
}

export function collectOpcionesFiltroGuardadas(rows) {
  const nfs = new Set();
  const dias = new Set();
  const ocs = new Set();
  const tiposNf = new Set();
  const grados = new Set();
  const proveedores = new Set();
  const almacenes = new Set();
  const productos = new Set();
  const espesores = new Set();
  const anchos = new Set();
  const largos = new Set();
  const bookings = new Set();
  const tiposMovimiento = new Set();
  const disponibilidades = new Set();
  const meses = new Set();
  const anios = new Set();

  for (const r of rows || []) {
    if (r.nro_nf != null && r.nro_nf !== '') nfs.add(String(r.nro_nf));
    if (r.oc_label) ocs.add(String(r.oc_label));
    if (r.tipo_nf != null && r.tipo_nf !== '') tiposNf.add(String(r.tipo_nf));
    if (r.grado) grados.add(String(r.grado));
    if (r.proveedor) proveedores.add(String(r.proveedor));
    if (r.almacen) almacenes.add(String(r.almacen));
    if (r.producto_nombre) productos.add(String(r.producto_nombre));
    if (r.espesor) espesores.add(String(r.espesor));
    if (r.ancho) anchos.add(String(r.ancho));
    if (r.largo) largos.add(String(r.largo));
    if (r.booking) bookings.add(String(r.booking));
    if (r.tipo_movimiento) tiposMovimiento.add(String(r.tipo_movimiento));
    if (r.estado_mercaderia) disponibilidades.add(String(r.estado_mercaderia));
    if (r.fecha_nf) {
      const d = new Date(`${String(r.fecha_nf).slice(0, 10)}T12:00:00`);
      if (!Number.isNaN(d.getTime())) {
        dias.add(String(d.getDate()));
        meses.add(String(d.getMonth() + 1));
        anios.add(String(d.getFullYear()));
      }
    }
  }

  return {
    nfs: uniqSorted([...nfs], true),
    dias: uniqSorted([...dias], true),
    ocs: uniqSorted([...ocs]),
    tiposNf: uniqSorted([...tiposNf], true),
    grados: uniqSorted([...grados]),
    proveedores: uniqSorted([...proveedores]),
    almacenes: uniqSorted([...almacenes]),
    productos: uniqSorted([...productos]),
    espesores: uniqSorted([...espesores], true),
    anchos: uniqSorted([...anchos], true),
    largos: uniqSorted([...largos], true),
    bookings: uniqSorted([...bookings]),
    tiposMovimiento: uniqSorted([...tiposMovimiento]),
    disponibilidades: uniqSorted([...disponibilidades]),
    meses: uniqSorted([...meses], true),
    anios: uniqSorted([...anios], true),
  };
}

export function buildOpcionesFiltroCascadaGuardadas(base, filtros) {
  const pick = (campo) =>
    collectOpcionesFiltroGuardadas(aplicarFiltrosGuardadas(base, filtrosExcluyendo(filtros, campo)));
  return {
    nfs: pick('nf').nfs,
    dias: pick('dia').dias,
    meses: pick('mes').meses,
    anios: pick('anio').anios,
    ocs: pick('oc').ocs,
    tiposNf: pick('tipoNf').tiposNf,
    grados: pick('grado').grados,
    espesores: pick('espesor').espesores,
    anchos: pick('ancho').anchos,
    largos: pick('largo').largos,
    proveedores: pick('proveedor').proveedores,
    almacenes: pick('almacen').almacenes,
    productos: pick('producto').productos,
    tiposMovimiento: pick('tipoMovimiento').tiposMovimiento,
    disponibilidades: pick('disponibilidadProducto').disponibilidades,
    bookings: pick('booking').bookings,
  };
}

export function opcionesMesSelector(mesesEnDatos) {
  if (mesesEnDatos?.length > 0) return mesesEnDatos;
  return MESES_NOMBRE.map((m) => m.value);
}

export function opcionesDiaSelector(diasEnDatos) {
  if (diasEnDatos?.length > 0) return diasEnDatos;
  return Array.from({ length: 31 }, (_, i) => String(i + 1));
}

export function opcionesAnioSelector(aniosEnDatos) {
  if (aniosEnDatos?.length > 0) return aniosEnDatos;
  const y = new Date().getFullYear();
  return [String(y), String(y - 1), String(y - 2), String(y - 3)];
}

export function hayFiltrosActivos(filtros) {
  return Object.values({ ...FILTROS_GUARDADAS_VACIOS, ...filtros }).some((v) => String(v || '').trim() !== '');
}

export function FilterAutocomplete({ label, value, onChange, options, placeholder, idSuffix }) {
  const listId = `dl-inv-g-${idSuffix}`;
  return (
    <Form.Group className="mb-0" style={filterFieldStyle}>
      <Form.Label className="small mb-1">{label}</Form.Label>
      <Form.Control
        size="sm"
        className="w-100"
        type="text"
        list={listId}
        autoComplete="off"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <datalist id={listId}>
        {(options || []).map((opt) => (
          <option key={opt} value={opt} />
        ))}
      </datalist>
    </Form.Group>
  );
}

export function FilterSelect({ label, value, onChange, options, renderOption, emptyLabel, idSuffix }) {
  return (
    <Form.Group className="mb-0" style={filterFieldStyle}>
      <Form.Label className="small mb-1">{label}</Form.Label>
      <Form.Select
        size="sm"
        className="w-100"
        id={idSuffix ? `sel-inv-g-${idSuffix}` : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{emptyLabel ?? 'Todos'}</option>
        {(options || []).map((opt) => (
          <option key={opt} value={opt}>
            {renderOption ? renderOption(opt) : opt}
          </option>
        ))}
      </Form.Select>
    </Form.Group>
  );
}

export { TIPOS_MOVIMIENTO_GESTION as TIPOS_MOVIMIENTO, ESTADOS_DISPONIBILIDAD };
